import { useMessage } from '@/hooks/useMessage';
import { SettingsContainer } from '@/node-editor/components';
import { useNodeContext, useNodeDialog } from '@/node-editor/hooks';
import { debounce } from '@mui/material';
import {
  removeNode,
  reorderNode,
  setCurrentNode,
  updateNode,
  useActions,
  addTemplate,
} from '@/node-editor/store/actions';
import {
  filterSavedTemplateData,
  fromJsonTemplate,
} from '@/node-editor/utilities';
import { getNodeOverride } from '@/node-editor/store/selectors';
import { validateNodeProperties } from '@/node-editor/validation';
import { addNodeTemplateService } from '@/services/nodeTemplateService';
import { INode, INodeTemplate } from '@/types/node';
import {
  Box,
  Card,
  CardContent,
  Icon,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {
  FC,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Draggable } from 'react-beautiful-dnd';

const TypedDraggable = Draggable as any;
// @ts-ignore - Workaround for Draggable typing issue
import { FieldValues } from 'react-hook-form';
import { EditorWindowContext } from '@/node-editor/components/preview/editor-window';
import { useTheme, Theme } from '@mui/material/styles';

interface IProps {
  node: INode;
  template: INodeTemplate;
  children:
    | ReactNode
    | ReactNode[]
    | ((provided: {
        supportsChildren: boolean;
        reorderNode: (clientId: string, from: number, to: number) => void;
      }) => ReactNode);
  error?: boolean;
  slot?: string | JSX.Element;
}

interface IStyleProps {
  hasChildren: boolean;
  hasError: boolean;
}

const useStyles = (props: IStyleProps, theme: Theme) => {
  const { hasChildren, hasError } = props;
  return {
    root: {
      backgroundColor: hasChildren
        ? `${theme.palette.grey[200]} !important`
        : undefined,
      borderRadius: '4px !important',
      padding: '0 !important',
      paddingLeft: '2px !important',
      paddingRight: '2px !important',
      paddingBottom: hasChildren
        ? `${theme.spacing(1)} !important`
        : '0 !important',
      marginBottom: '4px !important', //theme.spacing(1),
      border: '1px solid !important',
      borderColor: hasError
        ? `${theme.palette.error.main} !important`
        : `${theme.palette.grey[400]} !important`,
    },
    isCurrent: {
      borderColor: '#f00!important',
    },
    cardHeader: {
      display: 'flex !important',
      alignItems: 'center !important',
      flex: '1 1 100% !important',
      width: '100% !important',
      height: '30px !important',
      '&:hover': {
        '& > ._setting_': {
          display: 'inline !important',
        },
      },
    },
    settingsButton: {
      display: 'none !important',
    },
    nodeHeader: {
      flexGrow: '1 !important',
      overflow: 'hidden !important',
    },
    nodeInactive: {
      fontSize: '0.7rem !important',
      color: `${theme.palette.error.main} !important`,
      paddingTop: '2px !important',
    },
    spacing: {
      marginLeft: '3px !important', //theme.spacing(1),
    },
    content: {
      padding: '0 !important',
      '&:last-child': {
        paddingBottom: '0 !important',
      },
    },
    slot: {
      fontStyle: 'italic !important',
      fontSize: '0.8rem !important',
    },
    // Animation
    '@keyframes scaledown': {
      '0%': {
        transform: 'scale(3) !important',
      },
      '100%': {
        transform: 'scale(1) !important',
      },
    },
    anim: {
      border: '1px solid #f00!important',
      animation: '$scaledown 1s ease-in-out 1 !important',
    },
  };
};

export const NodeControls: FC<IProps> = React.memo(
  ({ children, error, node, slot, template }) => {
    const [showAnimation, setShowAnimation] = useState(false);
    const actions = useActions(() => ({
      updateNode,
      removeNode,
      reorderNode,
      setCurrentNode,
      addTemplate,
    }));

    const { current } = useNodeContext();

    const { templates, openSettings, openAppender } = useNodeDialog();

    const handleRemove = useCallback(() => actions.removeNode(node), [
      actions,
      node,
    ]);

    const handleSettings = useCallback(() => {
      // Extract node content to use as default values
      const defaultValues = { 
        ...node.content,
        // Include override with merged default values
        override: getNodeOverride(node.type, (node as any).override || {}),
      };
      
      return openSettings({
        title: `Edit ${template.name}`,
        content: <SettingsContainer.Fill id={node.id} />,
        submitLabel: 'Apply',
        defaultValues, // Pass node properties as default values
        finishedAction: () => {
          setShowAnimation(true);
          setTimeout(() => {
            setShowAnimation(false);
          }, 2000);
        },
        submitAction: (values: FieldValues) => {
          try {
            actions.updateNode(validateNodeProperties(node, values));
          } catch (error) {
            console.error('Failed to update node:', error);
            throw error; // Re-throw to prevent dialog from closing on error
          }
        },
        saveAsTemplate: (
          { title, description, picture, public: showPublic }: FieldValues,
          conferenceId: string
        ) => {
          const nodeTemplate: Partial<INodeTemplate[]> = [
            (node as unknown) as INodeTemplate,
          ] as Partial<INodeTemplate[]>;
          const nodeList = filterSavedTemplateData(
            fromJsonTemplate(nodeTemplate),
            conferenceId
          );

          addNodeTemplateService({
            title,
            description,
            picture,
            public: showPublic,
            type: node.type,
            nodes: nodeList,
          });
        },
        duplicate: () => {
          const nodeTemplate: Partial<INodeTemplate[]> = [
            (node as unknown) as INodeTemplate,
          ] as Partial<INodeTemplate[]>;
          const nodeList = fromJsonTemplate(nodeTemplate);
          actions.addTemplate(nodeList, node.nodeId);
        },
      });
    }, [openSettings, template.name, node.id, actions, node]);

    const handleEditCallback = useCallback(
      (_returnCallback: any, data: any) => {
        if (data.type === 'editcallback' && data.nodeId === node.id) {
          if (data.action === 'hover') {
            actions.setCurrentNode(node.id);
          } else if (data.action === 'edit') {
            handleSettings();
          } else if (data.action === 'add') {
            openAppender(data.nodeId);
          } else if (data.action === 'delete') {
            handleRemove();
          }
        }
      },
      [node.id, actions, openAppender, handleSettings, handleRemove]
    );

    // const { sendMessage } = useMessage();
    const { sendMessage } = useMessage('editcallback', handleEditCallback);

    const supportsChildren = useMemo(
      () => Boolean(templates[node?.type]?.supports?.children),
      [templates, node?.type]
    );

    const theme = useTheme();
    const classes = useMemo(
      () =>
        useStyles(
          {
            hasChildren: supportsChildren || error,
            hasError: error,
          },
          theme
        ),
      [supportsChildren, error, theme]
    );

    // Use a ref to track if we've already opened the dialog for this node
    const hasOpenedDialogRef = useRef<boolean>(false);

    useEffect(() => {
      if (node.justAdded && !hasOpenedDialogRef.current) {
        hasOpenedDialogRef.current = true; // Mark as opened immediately

        const { justAdded, ...withoutJustAdded } = node;
        actions.updateNode(withoutJustAdded);

        const openDialog = () => {
          try {
            handleSettings();
          } catch (e) {
            console.error('❌ Could not open edit window', e);
          }
        };

        // Use a longer timeout to ensure the node is properly updated
        const timeoutId = setTimeout(openDialog, 100);

        // Cleanup timeout on unmount
        return () => clearTimeout(timeoutId);
      }
    }, [node.justAdded, actions, node.id]); // Removed handleSettings from dependencies

    // Track the last node ID to detect when we have a truly new node
    const lastNodeIdRef = useRef<string | null>(null);

    // Reset the ref only when we have a truly new node (different ID) - use useEffect to prevent render loops
    useEffect(() => {
      if (lastNodeIdRef.current !== node.id) {
        hasOpenedDialogRef.current = false;
        lastNodeIdRef.current = node.id;
      }
    }, [node.id]);

    const slotTitle = useMemo(() => <span style={classes.slot}>{slot}</span>, [
      classes.slot,
      slot,
    ]);

    // Debounced mouse over handler to prevent rapid-fire updates
    const debouncedMouseOver = useCallback(
      debounce(
        ({
          contentWindow,
          slug,
        }: {
          contentWindow: HTMLIFrameElement | null;
          slug: string;
        }) => {
          // Only set current node if not already set to prevent unnecessary updates
          if (current !== node.id) {
            actions.setCurrentNode(node.id);
          }

          if (contentWindow?.contentWindow) {
            sendMessage(contentWindow.contentWindow, {
              type: 'preview_window',
              payload: {
                slug,
                type: 'CURRENT_NODE',
                payload: node.id,
              },
            });
          }
        },
        100
      ),
      [actions, node.id, sendMessage, current]
    );

    const handleMouseOver = useCallback(
      (params: { contentWindow: HTMLIFrameElement | null; slug: string }) => {
        debouncedMouseOver(params);
      },
      [debouncedMouseOver]
    );

    const handleMouseLeave = useCallback(
      ({
        contentWindow,
        slug,
      }: {
        contentWindow: HTMLIFrameElement | null;
        slug: string;
      }) => {
        actions.setCurrentNode('');

        if (contentWindow?.contentWindow) {
          sendMessage(contentWindow.contentWindow, {
            type: 'preview_window',
            payload: {
              slug,
              type: 'CURRENT_NODE',
              payload: '',
            },
          });
        }
      },
      [actions, sendMessage]
    );
    return (
      //new version

      <TypedDraggable draggableId={node.id} index={node.order || 0}>
        {(provided: any) => (
          <EditorWindowContext.Consumer>
            {(editorContext) => (
              <Card
                {...provided.draggableProps}
                ref={provided.innerRef}
                onMouseOverCapture={() =>
                  editorContext && handleMouseOver(editorContext)
                }
                onMouseOutCapture={() =>
                  editorContext && handleMouseLeave(editorContext)
                }
                role="listitem"
                title={template.name}
                sx={{
                  ...classes.root, // base styles
                  ...(current === node.id && classes.isCurrent),
                  ...(showAnimation && classes.anim),
                }}
              >
                <Box sx={classes.cardHeader}>
                  <Icon {...provided.dragHandleProps}>drag_handle</Icon>
                  <Icon sx={classes.spacing}>{template.icon}</Icon>
                  <Box sx={classes.nodeHeader}>
                    <Typography
                      noWrap
                      onClick={() => !error && handleSettings()}
                      sx={classes.spacing}
                      style={{ cursor: error ? 'initial' : 'pointer' }}
                    >
                      {template.name} {slotTitle}
                    </Typography>
                  </Box>
                  {node.hasOwnProperty('active') &&
                    (node as any).active === false && (
                      <div>
                        <Tooltip title="Not active">
                          <span>
                            <Icon sx={classes.nodeInactive}>
                              visibility_off
                            </Icon>
                          </span>
                        </Tooltip>
                      </div>
                    )}

                  <Box sx={classes.settingsButton} className="_setting_">
                    <IconButton
                      disabled={error}
                      size="small"
                      aria-label="open node settings"
                      onClick={handleSettings}
                    >
                      <Icon>settings</Icon>
                    </IconButton>
                  </Box>

                  <IconButton
                    size="small"
                    aria-label="remove node"
                    onClick={handleRemove}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                  {/* </Tooltip> */}
                </Box>
                <CardContent sx={classes.content}>
                  {typeof children === 'function'
                    ? children({
                        supportsChildren,
                        reorderNode: actions.reorderNode,
                      })
                    : children}
                </CardContent>
              </Card>
            )}
          </EditorWindowContext.Consumer>
        )}
      </TypedDraggable>
    );
  }
);
